import { SpinnerMask } from '@atlassian/amkt-common';
import {
  getSelectedAppVersion,
  isRuByPartnerAppAccessDeniedError,
  useAppListingById,
} from '@atlassian/mpac-app-listing';
import { HostingTypeToQueryParam } from '@atlassian/mpac-types';
import {
  AtlassianProductHostingType,
  MarketplaceEntityStatus,
  MarketplaceLocation,
} from '@atlassian/mpac-types/dist/GraphQLGateway.gql.types';
import { useHistory, useLocationHref } from '@atlassian/mpac-ui';
import { archivedAppLink, log, UrlUtils } from '@atlassian/mpac-utils';
import capitalize from 'lodash/capitalize';
import React, { useEffect } from 'react';

import NotFound from 'common/components/NotFound';
import RedirectExternal from 'common/components/RedirectExternal';
import RedirectToLogin from 'common/components/RedirectToLogin';
import ServerError from 'common/components/ServerError';
import { safeToForwardToPartner, trackTabChange } from 'common/utils/analytics/analytics';
import BootstrapConfig from '../../../../../../../bootstrapConfig';
import { AppListingPageTask } from '../../../../../../../telemetry/tasks';
import AtlassianSupportsUkraineRoute from '../../../../AtlassianSupportsUkraineRoute';
import { APP_LISTING_HOME_LINK } from '../../../constants';
import { getFirstValidHostingType, toAnalyticsHosting } from '../utils';
import AppListingPageContent from './AppListingPageContent';
import { Container } from './styles';
import { makeLinkTemplates } from './utils';

import type { ApolloError } from '@apollo/client';
import type { AppListingTabsType } from '@atlassian/mpac-app-listing';

const { updateQueryParam } = UrlUtils;

type AppListingPageProps = {
  appId: string;
  selectedHosting: AtlassianProductHostingType | null | undefined;
  tab: AppListingTabsType;
  showOnlyPublic: boolean;
};

const AppListingPage = (props: AppListingPageProps) => {
  const { appId, selectedHosting, showOnlyPublic } = props;
  const history = useHistory();
  const locationHref = useLocationHref();
  const links = makeLinkTemplates();
  const mpacBaseUrl = BootstrapConfig.get().mpacBaseUrl;

  const setNewHostingType = (newHosting: AtlassianProductHostingType) => {
    const updatedPathname = updateQueryParam(
      locationHref,
      'hosting',
      HostingTypeToQueryParam[newHosting]
    );

    // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
    history.replace(updatedPathname);
    safeToForwardToPartner(appId, () => {
      trackTabChange(updatedPathname, toAnalyticsHosting(newHosting), true);
    });
  };

  const setNewPageTab = (tab: AppListingTabsType) => {
    const newPath = updateQueryParam(locationHref, 'tab', tab);
    // @ts-expect-error [MC-2850] - TS2532 - Object is possibly 'undefined'.
    history.replace(newPath);
    safeToForwardToPartner(appId, () => {
      trackTabChange(newPath, capitalize(tab), true);
    });
  };

  // run this effect before any other hooks
  useEffect(() => {
    AppListingPageTask.start();
  }, []);

  const { data, error, loading } = useAppListingById({
    appId,
    location: MarketplaceLocation.Website,
    showOnlyPublic,
  });

  if (error) {
    if (data?.entityStatus === MarketplaceEntityStatus.Archived) {
      return redirectArchivedApp(mpacBaseUrl, appId);
    }
    return handleApolloError(error, locationHref);
  }

  const resolveAppListingContent = () => {
    const notFound = <NotFound homeLink={APP_LISTING_HOME_LINK} />;
    if (!data) {
      // return null -> actually loading and waiting for response. the spinner would be shown
      // return notFound -> if not loading and still data is false, it means this app does not exist
      return loading ? null : notFound;
    }

    if (data.entityStatus === MarketplaceEntityStatus.Archived) {
      return redirectArchivedApp(mpacBaseUrl, appId);
    }

    const firstValidHosting = selectedHosting || getFirstValidHostingType(data);
    if (firstValidHosting !== selectedHosting) {
      setNewHostingType(firstValidHosting);
    }

    const selectedAppVersion = getSelectedAppVersion(firstValidHosting, data);

    if (!selectedAppVersion) {
      if (data && data.allServerDCAppVersions.totalCount > 0) {
        const versionHistoryUrl = links.appVersionHistory.expand({
          appId: props.appId,
          appSlug: data.slug,
        });
        AppListingPageTask.abort();
        return <RedirectExternal statusCode={302} url={versionHistoryUrl} />;
      }

      return notFound;
    }

    return (
      <AppListingPageContent
        appDetails={data}
        appId={props.appId}
        tab={props.tab}
        links={links}
        selectedHosting={firstValidHosting}
        selectedAppVersion={selectedAppVersion}
        setNewHostingType={setNewHostingType}
        setNewTab={setNewPageTab}
        showOnlyPublic={showOnlyPublic}
      />
    );
  };

  // Show a spinner only when we don't have data
  // from the graphql cache available immediately
  const showSpinner = !data && loading;

  return (
    <SpinnerMask active={showSpinner} delay={1000} size={50}>
      <Container>{resolveAppListingContent()}</Container>
    </SpinnerMask>
  );
};

const redirectArchivedApp = (mpacBaseUrl, appId) => {
  const archivedAppUrl = archivedAppLink(mpacBaseUrl).expand({ appId });
  log.info('AppListingPage', `App is archived, redirecting to archived app url, ${archivedAppUrl}`);
  AppListingPageTask.abort();
  return <RedirectExternal statusCode={302} url={archivedAppUrl} />;
};

const handleApolloError = (error: ApolloError, locationHref: string) => {
  /**
   * When a logged-in users' session expires, the gateway treats it as failed-auth instead of
   * downgrading it to an unauthenticated call. Temporarily, we will redirect 401 responses
   * to the login page, for the app listing page.
   * See https://ecosystem.atlassian.net/browse/MC-1770
   */
  if (
    error.networkError &&
    'statusCode' in error.networkError &&
    error.networkError.statusCode === 401
  ) {
    log.info('AppListingPage', 'Auth failure while fetching app listing, redirecting to login');
    AppListingPageTask.abort();
    return <RedirectToLogin />;
  }

  if (isRuByPartnerAppAccessDeniedError(error)) {
    log.info('AppListingPage', `Prevented App Listing Access to RuBy partner App, ${locationHref}`);
    AppListingPageTask.abort();
    return <AtlassianSupportsUkraineRoute />;
  }

  return <ServerError homeLink={APP_LISTING_HOME_LINK} />;
};

export default AppListingPage;
